import { DateTime, Duration, Interval } from "luxon";

export const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const EXPLICIT_TIMEZONE_PATTERN = /(?:Z|[+-]\d{2}:\d{2})$/;

const DISPLAY_DATE_FORMAT = "MM/dd/yyyy";
const PRETTY_DATE_FORMAT = "MMM dd, yyyy";
const PARSE_INPUT_FORMATS = [
  DISPLAY_DATE_FORMAT,
  "M/d/yyyy",
  "MM/d/yyyy",
  "M/dd/yyyy",
  PRETTY_DATE_FORMAT,
  "MMM d, yyyy",
  "MMMM dd, yyyy",
  "MMMM d, yyyy"
];

export function toLocaleDate(date: string, format: Intl.DateTimeFormatOptions = DateTime.DATE_SHORT) {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return "";
  }
  return parsedDate.toLocaleString(format);
}

export function toUTCFormat(date: string, format: string = "LLL dd, yyyy") {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toUTC().toFormat(format);
}

export function toFormat(date: string, format: string = "LLL dd, yyyy") {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toFormat(format);
}

const EMBEDDED_ISO_DATE_PATTERN = /\b\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?\b/g;

/** Replaces ISO-8601 date/datetime tokens in free text with `toFormat` display strings. */
export function formatIsoDatesInText(text: string, format: string = "LLL dd, yyyy"): string {
  return text.replace(EMBEDDED_ISO_DATE_PATTERN, (iso) => toFormat(iso, format) ?? iso);
}

export const calculateYearsOfMembership = (days?: number) => {
  if (!days) return null;

  const now = DateTime.now();
  const startDate = now.minus({ days });
  const diff = now.diff(startDate, ["years", "months"], { conversionAccuracy: "longterm" });

  const years = Math.floor(diff.years ?? 0);
  const months = Math.floor(diff.months ?? 0);

  if (years === 0) {
    return `${months} mo`;
  }
  return `${years} yrs ${months} mos`;
};

/**
 * Calculates the time remaining until a deadline date.
 * Returns a human-readable string like "2 years, 3 months, 5 days" or "1 month, 2 days".
 * Returns null if the deadline is invalid, in the past, or no date is provided.
 *
 * Excludes zero values and rounds all numbers to integers (no decimals).
 *
 * @param deadlineDate - ISO date string, null, or undefined
 * @returns Formatted time remaining string or null
 */
export function calculateTimeRemaining(deadlineDate: string | null | undefined): string | null {
  if (!deadlineDate) return null;

  const deadline = DateTime.fromISO(deadlineDate);
  const now = DateTime.now();

  if (!deadline.isValid || deadline.startOf("day") < now.startOf("day")) {
    return null;
  }

  const diff = deadline.diff(now, ["years", "months", "days"], { conversionAccuracy: "longterm" });

  const years = Math.floor(diff.years ?? 0);
  const months = Math.floor(diff.months ?? 0);
  const days = Math.floor(diff.days ?? 0);

  const parts: string[] = [];

  if (years > 0) {
    parts.push(`${years} ${years === 1 ? "year" : "years"}`);
  }
  if (months > 0) {
    parts.push(`${months} ${months === 1 ? "month" : "months"}`);
  }
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  }

  if (parts.length === 0) {
    return null;
  }

  return parts.join(", ");
}

/**
 * Formats employment-style duration from start through end.
 * When {@link calculateMonthsBetween} is under 1 month, uses calendar days (e.g. "15 days").
 * Otherwise whole years plus whole remaining months (e.g. "2 years, 3 months"), no day units.
 *
 * @param startDate - ISO date string (date or datetime)
 * @param endDate - ISO date string or `Date` (e.g. `new Date()` for through today)
 * @returns `null` if either date is invalid or the end is before the start
 */
export function formatDurationBetweenDates(
  startDate: string | null | undefined,
  endDate: string | Date | null | undefined
): string | null {
  if (!startDate || endDate === null || endDate === undefined) {
    return null;
  }

  const start = DateTime.fromISO(startDate).startOf("day");
  if (!start.isValid) {
    return null;
  }

  const end =
    typeof endDate === "string"
      ? DateTime.fromISO(endDate).startOf("day")
      : DateTime.fromJSDate(endDate).startOf("day");
  if (!end.isValid) {
    return null;
  }

  if (end < start) {
    return null;
  }

  const totalMonths = calculateMonthsBetween(startDate, endDate);

  if (totalMonths < 1) {
    const dayCount = Math.floor(end.diff(start, "days").days);
    if (dayCount <= 0) {
      return "0 days";
    }
    return `${dayCount} ${dayCount === 1 ? "day" : "days"}`;
  }

  const fullYears = Math.floor(totalMonths / 12);
  const monthRemainder = Math.floor(totalMonths - fullYears * 12);

  const parts: string[] = [];

  if (fullYears > 0) {
    parts.push(`${fullYears} ${fullYears === 1 ? "year" : "years"}`);
  }

  if (monthRemainder > 0 || fullYears === 0) {
    const label = monthRemainder === 1 ? "month" : "months";
    parts.push(`${monthRemainder} ${label}`);
  }

  return parts.join(", ");
}

export function toDateTimeFormat(date: string, format: string = "LLL dd, yyyy t") {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toFormat(format);
}

const GENERIC_US_TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  EST: "ET",
  EDT: "ET",
  CST: "CT",
  CDT: "CT",
  MST: "MT",
  MDT: "MT",
  PST: "PT",
  PDT: "PT",
  AKST: "AKT",
  AKDT: "AKT",
  HST: "HT",
  HDT: "HT"
};

// Major US zones map directly off the IANA name — locale-independent, so
// non-English browsers (where offsetNameShort is GMT-style or localized) still
// resolve. Remaining US aliases resolve through the short-name map below in
// English browsers, else fall back to a UTC offset label.
const CANONICAL_US_ZONE_ABBREVIATIONS: Record<string, string> = {
  "America/New_York": "ET",
  "America/Detroit": "ET",
  "America/Indianapolis": "ET",
  "America/Chicago": "CT",
  "America/Denver": "MT",
  "America/Boise": "MT",
  "America/Phoenix": "MT",
  "America/Los_Angeles": "PT",
  "America/Anchorage": "AKT",
  "Pacific/Honolulu": "HT"
};

function abbreviationForParsedDateTime(parsed: DateTime): string {
  const canonical = CANONICAL_US_ZONE_ABBREVIATIONS[parsed.zoneName ?? ""];
  if (canonical) return canonical;
  const short = parsed.offsetNameShort;
  if (short && GENERIC_US_TIMEZONE_ABBREVIATIONS[short]) return GENERIC_US_TIMEZONE_ABBREVIATIONS[short];
  return formatUtcOffsetLabel(parsed.offset);
}

/**
 * Generic viewer-local timezone abbreviation (ET/CT/MT/PT/AKT/HT).
 * Collapses standard/daylight variants on purpose — no CST/CDT split.
 * Non-US zones fall back to a stable UTC offset label (e.g. UTC+9).
 */
export function getGenericTimezoneAbbreviation(iso: string, zone?: string): string | null {
  const parsed = zone ? DateTime.fromISO(iso, { zone }) : DateTime.fromISO(iso);
  if (!parsed.isValid) return null;
  return abbreviationForParsedDateTime(parsed);
}

/** Stable locale-independent offset label: UTC, UTC+9, UTC-5, UTC+5:30. */
export function formatUtcOffsetLabel(offsetMinutes: number): string {
  if (!Number.isFinite(offsetMinutes) || offsetMinutes === 0) return "UTC";
  const sign = offsetMinutes < 0 ? "-" : "+";
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return minutes === 0 ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${minutes.toString().padStart(2, "0")}`;
}

/**
 * Local datetime plus a generic timezone abbreviation, e.g. "Sep 21, 2026 12:21 PM CT".
 * Pass `zone` only to force a zone in tests — production callers use viewer-local time.
 */
export function toDateTimeFormatWithTimezone(
  date: string,
  format: string = "LLL dd, yyyy t",
  zone?: string
): string | null {
  const parsed = zone ? DateTime.fromISO(date, { zone }) : DateTime.fromISO(date);
  if (!parsed.isValid) return null;
  return `${parsed.toFormat(format)} ${abbreviationForParsedDateTime(parsed)}`;
}

export type DateFormatOptions = {
  format?: string;
  tz?: string;
};

export function toDateFormat(date: string, options: DateFormatOptions = {}) {
  const { format, tz } = options;
  const datetimeOptions = tz ? { zone: tz } : {};
  const parsedDate = DateTime.fromISO(date, datetimeOptions);
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toFormat(format || "LLL dd, yyyy");
}

export function toDateOnlyInTimezone(date: string | Date | null | undefined, timezone: string): string | undefined {
  if (!date) return undefined;

  if (typeof date === "string") {
    if (DATE_ONLY_PATTERN.test(date)) {
      return date;
    }

    const parsedDate = EXPLICIT_TIMEZONE_PATTERN.test(date)
      ? DateTime.fromISO(date, { setZone: true }).setZone(timezone)
      : DateTime.fromISO(date, { zone: timezone });

    return parsedDate.isValid ? parsedDate.toFormat("yyyy-MM-dd") : undefined;
  }

  const parsedDate = DateTime.fromJSDate(date, { zone: timezone });
  return parsedDate.isValid ? parsedDate.toFormat("yyyy-MM-dd") : undefined;
}

export function getTime(
  date: string,
  format: Intl.DateTimeFormatOptions = DateTime.TIME_SIMPLE,
  timezone: string = "utc"
) {
  const parsedDate = DateTime.fromISO(date, { zone: timezone });
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toLocaleString(format);
}

export function getHours(duration: string) {
  const parsedTime = Duration.fromISO(duration);
  if (!parsedTime.isValid) {
    return null;
  }

  let humanDuration = parsedTime.toHuman({ unitDisplay: "short" });
  return humanDuration.replace(/,/g, "");
}

/** Formats a date-only ISO string as `MM/dd/yyyy` for focused DateInput typing. */
export function formatDisplayDate(dateString: string | null | undefined): string {
  if (!dateString) return "";
  return toFormat(dateString, DISPLAY_DATE_FORMAT) || "";
}

/** Formats a date-only ISO string as `MMM dd, yyyy` for unfocused DateInput display. */
export function formatPrettyDate(dateString: string | null | undefined): string {
  if (!dateString) return "";
  return toFormat(dateString, PRETTY_DATE_FORMAT) || "";
}

/**
 * Parses typed, pretty, or ISO date strings into `yyyy-MM-dd`.
 * Rejects impossible calendar dates (for example Feb 31).
 */
export function parseInputDate(inputValue: string | null | undefined): string | null {
  if (!inputValue?.trim()) return null;
  const trimmed = inputValue.trim();

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    const date = DateTime.fromISO(trimmed);
    return date.isValid ? trimmed : null;
  }

  // Full ISO datetimes only — fromISO("01") is a valid time of *today*.
  if (trimmed.includes("T") || EXPLICIT_TIMEZONE_PATTERN.test(trimmed)) {
    const fromIso = DateTime.fromISO(trimmed);
    if (fromIso.isValid) return fromIso.toFormat("yyyy-MM-dd");
  }

  for (const format of PARSE_INPUT_FORMATS) {
    const date = DateTime.fromFormat(trimmed, format);
    if (date.isValid) return date.toFormat("yyyy-MM-dd");
  }

  return null;
}

export function isDateOnlyInRange(isoDate: string, minDate?: Date, maxDate?: Date): boolean {
  const date = DateTime.fromISO(isoDate).startOf("day");
  if (!date.isValid) return false;
  if (minDate && date < DateTime.fromJSDate(minDate).startOf("day")) return false;
  if (maxDate && date > DateTime.fromJSDate(maxDate).startOf("day")) return false;
  return true;
}

/** Local calendar Date for a `yyyy-MM-dd` value — never `new Date("yyyy-MM-dd")` (UTC shift). */
export function dateOnlyToJsDate(isoDate: string | null | undefined): Date | undefined {
  if (!isoDate || !DATE_ONLY_PATTERN.test(isoDate)) return undefined;
  const date = DateTime.fromISO(isoDate);
  return date.isValid ? date.toJSDate() : undefined;
}

export function toISO(date: string) {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return "";
  }
  return parsedDate.toISO();
}

export function getAge(date: string): Duration | null {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return null;
  }
  return Interval.fromDateTimes(parsedDate, DateTime.now()).toDuration(["years", "months", "days", "hours"]);
}

export const getDiffYears = (date: string) => DateTime.now().diff(DateTime.fromISO(date), ["years"]).rescale().years;

export function isInFuture(date: string) {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return false;
  }
  return parsedDate > DateTime.now();
}

/**
 * Checks if a date is before today (comparing at day level, ignoring time).
 * Returns true if the date is before today, false if it's today or in the future.
 *
 * @param date - ISO date string
 * @returns true if date is before today, false otherwise
 */
export function isBeforeToday(date: string | null | undefined): boolean {
  if (!date) return false;

  const parsedDate = DateTime.fromISO(date).startOf("day");
  if (!parsedDate.isValid) {
    return false;
  }

  const today = DateTime.now().startOf("day");
  return parsedDate < today;
}

export function getOverdueDays(date: string | null | undefined): number {
  if (!isBeforeToday(date)) return 0;
  return Math.floor(DateTime.now().startOf("day").diff(DateTime.fromISO(date!).startOf("day"), "days").days);
}

export function isValidDate(value: any, format: string = "LLL dd, yyyy"): boolean {
  if (typeof value !== "string") {
    return false;
  }
  const date = DateTime.fromFormat(value, format);
  return date.isValid;
}

export function isSameDay(startDate: string, endDate: string, timezone?: string): boolean {
  const startDateTime = timezone ? DateTime.fromISO(startDate, { zone: timezone }) : DateTime.fromISO(startDate);
  const endDateTime = timezone ? DateTime.fromISO(endDate, { zone: timezone }) : DateTime.fromISO(endDate);

  if (!startDateTime.isValid || !endDateTime.isValid) {
    return false;
  }

  return startDateTime.hasSame(endDateTime, "day");
}

/**
 * Replicates Oracle's MONTHS_BETWEEN: uses a fixed 31-day month for the
 * fractional portion, and returns an exact integer when both dates fall on
 * the same day-of-month or both are the last day of their respective months.
 *
 * Extracts year/month/day from strings directly instead of constructing a
 * Date object, because `new Date("2024-01-31")` is parsed as UTC midnight
 * and `.getDate()` returns the local-timezone day (e.g. 30 in US Central).
 */
export function calculateMonthsBetween(startDate: string | Date, endDate: string | Date): number {
  const start = extractDateParts(startDate);
  const end = extractDateParts(endDate);
  if (!start || !end) return 0;

  const wholeMonths = (end.year - start.year) * 12 + (end.month - start.month);

  const startIsLastDay = start.day === daysInMonth(start.year, start.month);
  const endIsLastDay = end.day === daysInMonth(end.year, end.month);

  if (start.day === end.day || (startIsLastDay && endIsLastDay)) {
    return wholeMonths;
  }

  return Math.round((wholeMonths + (end.day - start.day) / 31) * 10) / 10;
}

function extractDateParts(date: string | Date): { year: number; month: number; day: number } | null {
  if (typeof date === "string") {
    const [year, month, day] = date.split("T")[0].split("-").map(Number);
    if (!year || !month || !day) return null;
    return { year, month, day };
  }
  if (isNaN(date.getTime())) return null;
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}
